import { BigNumber } from "bignumber.js";
import { getActiveLcSettings, POINTS_PER_LC } from './lc_settings';

/**
 * Point Conversion Utilities for Blockchain Transactions
 * 
 * This utility handles the precise conversion of LC points to Wei format
 * for smart contract interactions, eliminating precision loss issues.
 */

// LC Token Configuration
const LC_DECIMALS = 18; // Standard ERC-20 decimals
const WEI_MULTIPLIER = new BigNumber('1e18'); // 10^18 for Wei conversion

/**
 * Convert LC points to Wei format for smart contract
 * @param {number|string} lcAmount - Amount in LC (e.g., 0.003, 0.007)
 * @returns {string} - Wei amount as string for smart contract
 */
export const convertLCToWei = (lcAmount) => {
  try {
    const amount = new BigNumber(lcAmount);
    const weiAmount = amount.multipliedBy(WEI_MULTIPLIER);
    return weiAmount.toFixed(0); // Return as integer string
  } catch (error) {
    return '0';
  }
};

/**
 * Convert Wei back to LC for display
 * @param {string|number} weiAmount - Amount in Wei
 * @returns {string} - LC amount as string
 */
export const convertWeiToLC = (weiAmount) => {
  try {
    const amount = new BigNumber(weiAmount);
    const lcAmount = amount.dividedBy(WEI_MULTIPLIER);
    return lcAmount.toFixed(6); // 6 decimal places for display
  } catch (error) {
    return '0';
  }
};

/**
 * Convert internal point system to LC decimal
 * @param {number} points - Internal points (e.g., 7 = 0.007 LC, 3 = 0.003 LC)
 * @returns {string} - LC amount as decimal string
 */
export const convertPointsToLC = (points) => {
  return new BigNumber(points).dividedBy(1000).toFixed(6);
};

/**
 * Convert internal points directly to Wei (bypassing LC decimal)
 * @param {number} points - Internal points (e.g., 7, 3, 14, 6)
 * @returns {string} - Wei amount as string for smart contract
 */
export const convertPointsToWei = (points) => {
  const lcAmount = convertPointsToLC(points);
  return convertLCToWei(lcAmount);
};

/**
 * Format points for display to user
 * @param {number} points - Internal points
 * @returns {string} - Formatted display string (e.g., "0.007 LC")
 */
export const formatPointsForDisplay = (points) => {
  const lcAmount = convertPointsToLC(points);
  return `${lcAmount} LC`;
};

/**
 * Prepare point data for blockchain transaction
 * @param {number} points - Internal points
 * @returns {object} - Object containing both display and blockchain formats
 */
export const preparePointsForBlockchain = (points) => {
  const lcAmount = convertPointsToLC(points);
  const weiAmount = convertPointsToWei(points);
  
  return {
    points: points,
    lcAmount: lcAmount,
    weiAmount: weiAmount,
    displayFormat: formatPointsForDisplay(points)
  };
};

// Dynamic point configuration (numbers returned in POINTS, not LC)
// We use getters so existing imports keep working while values reflect the active LC level
export const POINT_CONFIG = {
  get LC_DECIMALS() { return LC_DECIMALS; },
  get POINTS_TO_LC_DIVISOR() { return POINTS_PER_LC; },
  get LIKE_POINTS() { return Number((getActiveLcSettings().likeGivenLc * POINTS_PER_LC).toFixed(3)); },
  get AUTHOR_POINTS() { return Number((getActiveLcSettings().likeReceivedLc * POINTS_PER_LC).toFixed(3)); },
  get COMMENT_LIKER_POINTS() { return Number((getActiveLcSettings().commentGivenLc * POINTS_PER_LC).toFixed(3)); },
  get COMMENT_AUTHOR_POINTS() { return Number((getActiveLcSettings().commentReceivedLc * POINTS_PER_LC).toFixed(3)); }
};